import { BaseService } from '../BaseService';
import apiClient from '../apiClient';
import { ApiResponse, User } from '../types';

export class UserService extends BaseService<
  User,
  Partial<User>,
  Partial<User>
> {
  constructor() {
    super('/users');
  }

  // Get users by role
  async getByRole(role: string): Promise<User[]> {
    const response = await apiClient.get<ApiResponse<User[]>>(
      `${this.endpoint}/role/${role}`
    );
    return response.data.data;
  }

  // Get active users only
  async getActiveUsers(): Promise<User[]> {
    const response = await apiClient.get<ApiResponse<User[]>>(
      `${this.endpoint}/active`
    );
    return response.data.data;
  }

  // Update user status
  async updateStatus(
    id: string,
    status: 'active' | 'inactive' | 'suspended'
  ): Promise<User> {
    const response = await apiClient.patch<ApiResponse<User>>(
      `${this.endpoint}/${id}/status`,
      { status }
    );
    return response.data.data;
  }

  // Update user role
  async updateRole(id: string, role: string): Promise<User> {
    const response = await apiClient.patch<ApiResponse<User>>(
      `${this.endpoint}/${id}/role`,
      { role }
    );
    return response.data.data;
  }

  // Search users with filters
  async searchWithFilters(filters: {
    query?: string;
    role?: string;
    status?: string;
    department?: string;
  }): Promise<User[]> {
    const response = await apiClient.get<ApiResponse<User[]>>(
      `${this.endpoint}/search`,
      { params: filters }
    );
    return response.data.data;
  }

  // Get users by department
  async getByDepartment(department: string): Promise<User[]> {
    const response = await apiClient.get<ApiResponse<User[]>>(
      `${this.endpoint}/department/${department}`
    );
    return response.data.data;
  }

  // Reset user password
  async resetPassword(id: string, newPassword: string): Promise<void> {
    await apiClient.patch(`${this.endpoint}/${id}/reset-password`, {
      password: newPassword
    });
  }

  // Update user profile (for current user)
  async updateProfile(userData: Partial<User>): Promise<User> {
    const response = await apiClient.patch<ApiResponse<User>>(
      `${this.endpoint}/profile`,
      userData
    );
    return response.data.data;
  }
}

// Create singleton instance
export const userService = new UserService();

// Export default for backward compatibility
export default userService;
